import type { VenueConfig } from "@aluume/shared/lib/types/venue";

/** Pool-category display name → the slug the availability API keys private
 *  slots by ("Canopy" → "canopy", "Mountain View" → "mountain-view").
 *  Single source for the mapping — used by /api/availability when grouping
 *  exclusive slots and by the staff schedule to preselect a category. */
export function categoryNameToSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, "-");
}

export const BATHE_CONFIG: VenueConfig = {
  venue: "bathe",
  brand: "Bathe",
  region: "Queenstown",
  // Durations offered (overlaid from the live schedule_templates by
  // loadVenueConfig). Defaults to 60-only (fixed-schedule launch); the customer
  // duration toggle only appears when >1 duration is configured.
  allowedDurations: [60],
  // FALLBACK ONLY — loadVenueConfig replaces this with the LIVE categories that
  // actually hold at least one active pool (an empty category is auto-hidden), so
  // the booking page never offers a category with nothing behind it. Kept in step
  // with the live data (Canopy was renamed Sanctuary; Botanical's pools moved to
  // Mountain View, leaving it empty) so even the offline fallback is truthful.
  privatePools: [
    { slug: "sanctuary", name: "Sanctuary", blurb: "Fully enclosed — private from the first breath.", img: "/pools/canopy.png" },
    { slug: "mountain-view", name: "Mountain View", blurb: "An open edge to the Remarkables.", img: "/pools/mountain-view.png" },
  ],
  communalSpace: {
    slug: "the-commons",
    name: "The Commons",
    blurb: "Our shared magnesium pool with the best Remarkables view on site. Adults 16+.",
    img: "/pools/commons.png",
  },
  allowChildren: true,
  // Fallback defaults — loadVenueConfig overlays the live Pool Settings values.
  // Bathe private pool: 4 adult spots, child = 0.65 of a spot → 4 adults, or
  // 2 adults + 3 children (the previous hard-coded rule, now DB-driven).
  privateMaxAdults: 4,
  communalMaxAdults: 8,
  privateMaxChildren: 3,
  privateGuestUnits: 4,
  privateChildWeight: 0.65,
  privateDesc: "Your own pool",
  privatePrice: "from $85",
  communalDesc: "The Commons",
  communalPrice: "from $55",
  perPerson: "$55",
  privateTitle: "Book your soak",
  privateSub: "Quiet time, all your own. Pick your pool, your moment, who’s coming — we’ll have everything ready.",
  communalTitle: "Book the Commons",
  communalSub: "A shared soak with the best Remarkables view on site. From $55 per person.",
  // The Remarkables — exclusive whole-pool hire of The Commons, 2–8 guests, 100%
  // private. The third experience on the booking page — "Exclusive" (Jeremy
  // 2026-10-29). The booked block is 90 minutes; customers are quoted the
  // 75-minute SOAK (the last 15 are on-site change time), so all copy here says 75.
  // Prices are tier-adjusted and come from the remarkables_pricing table via
  // get_remarkables_availability (not baked in here).
  remarkables: {
    title: "The Remarkables",
    desc: "Our signature pool",
    sub: "The whole pool to yourselves — the best Remarkables view on site, no one else in the water. Seventy-five private minutes for two to eight.",
    pool: {
      slug: "remarkables",
      name: "Remarkables",
      blurb: "The best view at Bathe in the largest pool. Enjoy uninterrupted views over Queenstown out to the Remarkables mountain range. This experience includes premium drinks and a take-home gift pack.",
      img: "/pools/commons.png",
    },
    minParty: 2,
    maxParty: 8,
    soakMinutes: 75,
  },
};

// Thermae: private saunas have no sub-types (single space card); no children (16+).
// Venue street addresses + directions links (card a2e2f058). The confirmation
// page, .ics LOCATION and site footer were all hardcoded to Bathe — a Thermae
// confirmation sent guests to 34 Brecon Street. Keyed by venue slug; unknown
// slugs fall back to Bathe (the historical behaviour).
export const VENUE_ADDRESSES = {
  bathe: {
    label: "Bathe",
    address: "34 Brecon Street, Queenstown 9300",
    directionsUrl: "https://maps.google.com/?q=34+Brecon+Street+Queenstown",
  },
  thermae: {
    label: "Thermae",
    // "Queenstown Marina" is the marina's commercial name (the sanctioned
    // guest-facing form, per 0318_comm_templates_queenstown_marina) — it is
    // physically at Frankton, ~10 min drive from central Queenstown.
    address: "Queenstown Marina",
    directionsUrl: "https://maps.google.com/?q=Queenstown+Marina,+Queenstown",
  },
} as const;
export function venueAddress(slug?: string | null) {
  return VENUE_ADDRESSES[slug === "thermae" ? "thermae" : "bathe"];
}

export const THERMAE_CONFIG: VenueConfig = {
  venue: "thermae",
  brand: "Thermae",
  region: "Lake Wakatipu",
  allowedDurations: [60],
  privatePools: null,
  // Fallback copy mirrors the live Pool Settings categories (Jeremy's 2026-09-15
  // prod naming: Private Sanctuary / The Commons) — loadVenueConfig overlays the
  // live rows, so these only render if that read fails.
  privateSpace: {
    name: "Private Sanctuary",
    blurb: "Your own floating sanctuary on the lake, with private sauna, private cold plunge, and shared steam room. Up to 8 guests.",
    img: "/pools/commons.png",
  },
  communalSpace: {
    slug: "communal-sauna",
    name: "The Commons",
    blurb: "Access to our two communal saunas, steam room, sun deck and cold plunge. All floating on Lake Wakatipu.",
    img: "/pools/commons.png",
  },
  allowChildren: false,
  // Fallback defaults — loadVenueConfig overlays the live Pool Settings values.
  // Thermae is 16+ (no children), so the child fields are inert.
  privateMaxAdults: 6,
  // November build: the communal offering is 2 saunas + steam room COMBINED
  // (14 concurrent), but the largest party a single start slot can seat is 6
  // (per-slot entry caps) — so 6 is the honest online party ceiling; bigger
  // groups go through staff.
  communalMaxAdults: 6,
  privateMaxChildren: 0,
  privateGuestUnits: 6,
  privateChildWeight: 1,
  privateDesc: "Your own sauna",
  privatePrice: "from $350", // fallback only — loadVenueConfig quotes it live
  communalDesc: "Communal sauna",
  communalPrice: "from $75",
  perPerson: "$75",
  privateTitle: "Book the float",
  privateSub: "Your own floating sauna with steam room access and cold plunge. 16+.",
  communalTitle: "Book the float",
  communalSub: "Two floating saunas, steam room and cold plunge on the lake. 16+.",
};
